import { type JSX, useEffect, useState } from 'react';
import { ethereumAddressToAccountId } from '@chainflip/delegation';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import { useAccountEffect } from 'wagmi';
import {
  type PolkadotWallet,
  SelectPjsAccountList,
  SelectWalletList,
} from '@/shared/components/ConnectPolkadotWalletModal';
import { DashboardModalWithCloseButton, ModalContent } from '@/shared/components/dashboards/Modal';
import Callout from '@/shared/components/flip-ui-kit/Callout';
import { TokenLogo } from '@/shared/components/logo/TokenLogo';
import { usePolkadot, type InjectedAccountWithMeta } from '@/shared/hooks/usePolkadot';
import EyeIcon from '@/shared/icons/flip-ui-kit/small/Eye';
import { FLIP_SYMBOL } from '@/shared/utils/env';
import { padHexBytes } from '@/shared/utils/strings';
import metamask from '../../../public/wallet-logos/metamask.svg';
import phantom from '../../../public/wallet-logos/phantom.svg';
import polkadot from '../../../public/wallet-logos/polkadot.svg';
import rabby from '../../../public/wallet-logos/rabby.svg';
import subwallet from '../../../public/wallet-logos/subwallet.svg';
import trust from '../../../public/wallet-logos/trust.svg';
import ViewOnlyAccountModal from '../../ViewOnlyAccountModal';

type WalletType = 'evm' | 'native';

const logos = { metamask, phantom, polkadot, rabby, subwallet, trust };

type WalletName = keyof typeof logos;

const logosForWalletType: Record<WalletType, WalletName[]> = {
  evm: ['metamask', 'rabby', 'phantom', 'trust'],
  native: ['metamask', 'polkadot', 'subwallet'],
};

const WalletTypeButton = ({ type, onClick }: { type: WalletType; onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className="group flex flex-col gap-y-3 rounded-md border border-cf-gray-4 bg-cf-gray-3-5 p-4 transition hover:bg-cf-gray-4"
  >
    <div className="flex justify-between gap-x-4">
      <div className="flex flex-col items-start justify-center font-aeonikMedium">
        <span className="text-16 text-cf-white">Connect {type === 'evm' ? 'EVM' : 'Native'}</span>
        <span className="text-14 text-cf-light-2">
          {type === 'evm'
            ? 'Get started with a deposit of any asset'
            : `Get started with a ${FLIP_SYMBOL} deposit`}
        </span>
      </div>
      <div className="flex items-center">
        <div className="grid grid-cols-2 gap-1">
          {logosForWalletType[type].map((wallet) => {
            const Logo = logos[wallet];
            return (
              <div key={wallet} className="h-4">
                <Logo className="rounded-[3px]" />
              </div>
            );
          })}
        </div>
      </div>
    </div>
    {type === 'evm' && (
      <div className="flex items-center gap-x-1 text-left text-12 text-cf-light-3">
        <Callout fullWidth type="info" size="large">
          <div className="max-w-[370px]">
            Safe multisig wallets are not supported. Use a standard EOA wallet (e.g. MetaMask, Rabby
            etc.).
          </div>
        </Callout>
      </div>
    )}
  </button>
);

const NativeWalletModal = ({
  open,
  onClose,
  onSelectAccount,
}: {
  open: boolean;
  onClose: () => void;
  onSelectAccount: (account: InjectedAccountWithMeta) => void;
}) => {
  const [selectedExtension, setSelectedExtension] = useState<PolkadotWallet>();

  return (
    <DashboardModalWithCloseButton open={open} onClose={onClose}>
      <ModalContent title={selectedExtension ? 'Select Account' : 'Select Wallet'}>
        {selectedExtension === undefined ? (
          <SelectWalletList
            onSelectWallet={setSelectedExtension}
            onSelectAccount={(account) => {
              onSelectAccount(account);
              onClose();
            }}
          />
        ) : (
          <SelectPjsAccountList
            accountTypes={['liquidity_provider']}
            selectedExtension={selectedExtension}
            onSelect={(account) => {
              onSelectAccount(account);
              onClose();
            }}
          />
        )}
      </ModalContent>
    </DashboardModalWithCloseButton>
  );
};

export default function StateChainAccountCard({
  onCompleted,
}: {
  onCompleted: (selectedAccount: InjectedAccountWithMeta) => void;
}): JSX.Element {
  const [type, setType] = useState<WalletType>();
  const { openConnectModal, connectModalOpen } = useConnectModal();
  const [viewOnlyModalOpen, setViewOnlyModalOpen] = useState(false);
  const { selectAccount, setShouldFetchExtensionData } = usePolkadot();

  const handleConnect = (account: InjectedAccountWithMeta) => {
    selectAccount(account);
    setShouldFetchExtensionData(false);
    onCompleted(account);
    setType(undefined);
  };

  useEffect(() => {
    setShouldFetchExtensionData(type === 'native');
  }, [type]);

  useEffect(() => {
    if (!connectModalOpen) setType(undefined);
  }, [connectModalOpen]);

  useAccountEffect({
    onConnect(data) {
      if (type === 'evm') {
        handleConnect({
          idHex: padHexBytes(data.address, 32),
          idSs58: ethereumAddressToAccountId(data.address),
          meta: { name: data.connector.name, source: 'evm' },
          type: 'evm',
          evmAddress: data.address,
          onboarded: false,
        });
      }
    },
  });

  return (
    <>
      <div className="flex flex-col gap-6 rounded-md border border-cf-gray-4 bg-cf-gray-3 p-8">
        <div className="flex justify-center [mask-image:linear-gradient(90deg,rgba(0,0,0,0)_0%,rgba(0,0,0,0.6)_50%,rgba(0,0,0,0)_100%)]">
          {(['Flip', 'Usdc', 'Sol', 'Usdt', 'Btc', 'Eth'] as const).map((asset) => (
            <div
              key={asset}
              className="mx-3 flex h-9 w-9 items-center justify-center rounded-sm border border-[#2D2D2D] [background:linear-gradient(180deg,#252525_0%,#1D1D1D_100%)]"
            >
              <TokenLogo token={asset} size="medium" />
            </div>
          ))}
        </div>
        <div className="flex flex-col items-center">
          <span className="font-aeonikMedium text-[32px] text-cf-white">
            Put your assets to work
          </span>
          <span className="flex max-w-[360px] flex-col text-center text-16 text-cf-light-2">
            Enjoy limit orders, range orders, passive liquidity providing and much more - all in one
            single app.
          </span>
        </div>
        <div className="flex flex-col gap-y-4">
          <WalletTypeButton
            type="evm"
            onClick={() => {
              setType('evm');
              openConnectModal?.();
            }}
          />
          <WalletTypeButton type="native" onClick={() => setType('native')} />
        </div>
        <div className="flex flex-col gap-y-4">
          <div className="flex items-center gap-x-4">
            <span className="flex-1 border-b border-cf-gray-3-5" />
            <span className="text-12 text-cf-light-2">Just want to explore the app?</span>
            <span className="flex-1 border-b border-cf-gray-3-5" />
          </div>
          <button
            type="button"
            onClick={() => setViewOnlyModalOpen(true)}
            className="flex items-center justify-center gap-x-1 font-aeonikMedium text-12 text-cf-light-3 transition hover:text-cf-white"
          >
            <EyeIcon /> Go to view only mode
          </button>
        </div>
      </div>
      <ViewOnlyAccountModal open={viewOnlyModalOpen} onClose={() => setViewOnlyModalOpen(false)} />
      <NativeWalletModal
        open={type === 'native'}
        onClose={() => setType(undefined)}
        onSelectAccount={handleConnect}
      />
    </>
  );
}
